import { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { MarketingHeader, MarketingFooter, MButton } from "@/components/marketing";
import { getArticle, ARTICLES } from "@/data/articles";

const TEAL = "#22D3C5";

export default function ArticlePage() {
  const { slug } = useParams();
  const article = getArticle(slug);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!article) return <Navigate to="/inspiration" replace />;

  const related = ARTICLES.filter((a) => a.slug !== article.slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden" data-testid={`article-page-${article.slug}`}>
      <div className="grain" />
      <MarketingHeader />

      <article className="relative z-10 max-w-2xl mx-auto px-6 pt-16 pb-24">
        <Link to="/inspiration" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-10" data-testid="article-back-link">
          <ArrowLeft className="h-4 w-4" /> Alle artikler
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-5">
            <span style={{ color: TEAL }}>{article.eyebrow}</span>
            <span className="h-1 w-1 rounded-full bg-muted-foreground/50" />
            <span>{article.readTime} læsning</span>
          </div>
          <h1 className="font-display text-3xl sm:text-5xl font-medium tracking-tight text-balance leading-[1.08]">{article.title}</h1>
        </motion.div>
        <div className="mt-8 h-px bg-border" />

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-10 space-y-5">
          {article.blocks.map((b, i) => {
            if (b.type === "h") return <h2 key={i} className="font-display text-xl sm:text-2xl font-medium tracking-tight pt-4">{b.text}</h2>;
            if (b.type === "quote") return (
              <blockquote key={i} className="my-2 border-l-2 pl-5 font-display text-xl sm:text-2xl font-medium leading-snug text-foreground" style={{ borderColor: TEAL }}>
                {b.text}
              </blockquote>
            );
            if (b.type === "list") return (
              <ul key={i} className="space-y-3">
                {b.items.map((it) => (
                  <li key={it} className="flex gap-3 text-[17px] leading-relaxed text-foreground/90">
                    <Check className="h-5 w-5 shrink-0 mt-0.5" style={{ color: TEAL }} /> {it}
                  </li>
                ))}
              </ul>
            );
            return <p key={i} className="text-[17px] leading-relaxed text-muted-foreground">{b.text}</p>;
          })}
        </motion.div>

        {/* Relaterede artikler */}
        {related.length > 0 && (
          <div className="mt-16 pt-10 border-t border-border" data-testid="related-articles">
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-5">Læs også</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {related.map((a) => (
                <Link key={a.slug} to={`/artikler/${a.slug}`} data-testid={`related-article-${a.slug}`}
                  className="group rounded-2xl border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-foreground/25">
                  <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-3">
                    <span style={{ color: TEAL }}>{a.eyebrow}</span>
                    <span className="h-1 w-1 rounded-full bg-muted-foreground/50" />
                    <span>{a.readTime}</span>
                  </div>
                  <h3 className="font-display text-lg font-medium leading-snug">{a.title}</h3>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
                    Læs artiklen <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* CTA to Aicom AI */}
        <div className="mt-14 rounded-2xl border border-border bg-card p-8 text-center" data-testid="article-aicom-cta">
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">Aicom AI</p>
          <h3 className="font-display text-2xl sm:text-3xl font-medium tracking-tight text-balance">
            Mød din nye digitale <span style={{ color: TEAL }}>kommunikationsrådgiver</span>
          </h3>
          <p className="text-muted-foreground mt-4 max-w-lg mx-auto leading-relaxed">
            Bliv synlig, genkendelig og relevant - uden at starte fra nul hver gang. Aicom AI hjælper jer med at sige det rigtige, igen og igen.
          </p>
          <div className="mt-7 flex justify-center">
            <MButton to="/losninger/aicom-ai" size="lg" data-testid="article-aicom-btn">Se Aicom AI <ArrowRight className="h-4 w-4" /></MButton>
          </div>
        </div>
      </article>

      <MarketingFooter />
    </div>
  );
}
